import { registerInterceptor, FetcherInterceptor } from '@goldcast/api/backend';
import { registerInterceptor as registerVideoInterceptor } from '@goldcast/api/content';

import { getEnv } from '@/constants';
import loggerService from '@/services/logger.service';
import store from '@/store';
import { clearSession } from '@/utils/safeSignout';
import { replaceRoute } from '@/utils/routerAccess';

const backendUrl = getEnv('VUE_FETCHER_BACKEND_SERVER') ?? '';
const contentBackendUrl = getEnv('VUE_APP_CL_BACKEND_URL') ?? '';

type Register = typeof registerInterceptor;

function registerFetchInterceptors(url: string, register: Register): void {
  try {
    register(FetcherInterceptor.PRE_PROCESS_OPTIONS, ({ options }) => {
      const jwtToken = store.state.user?.accessToken;
      options.headers = {
        ...options.headers,
        ...(!!jwtToken && { Authorization: `Bearer ${jwtToken}` })
      };
      options.url = options.url.startsWith(url || '') ? options.url : `${url}${options.url}`;
      return options;
    });
    register(FetcherInterceptor.ON_ERROR, ({ error }) => {
      loggerService.error(error as Error);
    });
    register(FetcherInterceptor.POST_PROCESS_RESPONSE, async ({ response, options, retryFn }) => {
      const clonedResponse = response?.clone?.();
      if (clonedResponse && response?.status && response?.status >= 500) {
        let error: null | Error = null;
        try {
          error = new Error(JSON.stringify(await clonedResponse.json()));
        } catch {
          error = new Error('Internal server error');
        }
        error.name = '';
        (error as any).statusCode = response.status;
        throw error;
      } else if (clonedResponse && response && response.status !== 204) {
        const data = await clonedResponse.json();
        const error = new Error(data?.message ?? data?.detail ?? JSON.stringify(data));

        if (response.status === 401 && !options?.unAuthTries) {
          await store.dispatch('fetchAccessToken', { isUnauthorized: true, error: data.error }, { root: true });
          options.unAuthTries = 1;
          return retryFn?.(options);
        }
        if (response.status === 401 && options.unAuthTries && options.unAuthTries >= 1) {
          error.name = '';
          (error as any).statusCode = response.status;
          const eventId = store.state.event.event.id;
          const mattermostBaseUrl = getEnv('VUE_APP_API_MATTERMOST');
          if (
            store.state.event.useGCAuth &&
            store.state.event.isNewLoginFlowEnabled &&
            options.url.startsWith(mattermostBaseUrl)
          ) {
            loggerService.error(
              '[Mattermost Cognito Error]: 401 error with new login flow enabled when trying to fetch token',
              error
            );
          }
          clearSession();
          replaceRoute({
            path: '/auth',
            query: {
              eventID: eventId
            }
          });
          throw error;
        } else if (response.status >= 400) {
          error.name = '';
          (error as any).statusCode = response.status;
          (error as any).response = { data, status: response.status };
          if (data && data['code']) {
            (error as any).apiErrorCode = data['code'];
          }
          if (data?.detail) {
            (error as any).detail = data.detail;
          }
          if (data?.error) {
            (error as any).error = data.error;
          }
          if (data?.details) {
            (error as any).details = data.details;
          }
          throw error;
        }
      }
      return response;
    });
  } catch (error) {
    loggerService.error(error);
  }
}

export default function initializeFetchRegister(): void {
  try {
    registerFetchInterceptors(backendUrl, registerInterceptor);
    registerFetchInterceptors(contentBackendUrl, registerVideoInterceptor);
  } catch (error: any) {
    loggerService.error(error);
  }
}
